import type { LinkProvider } from "./blocks.types";

// Client-safe pure helpers for Content Card link metadata, shared by the
// OG-unfurl server fetcher (packages/data/src/server/manager/messaging/
// og-unfurl.ts), the email-import image screen (apps/web email-import), and
// the Content Card links editor/renderers (packages/messaging
// content-card-links.ts). ONE home on the existing dependency direction so
// provider detection, video classification, display-domain derivation, the
// syntactic URL screen, and HTML-entity decoding cannot drift between the
// snapshot persisted by the server and the editor's fallback path.

const PROVIDER_HOST_MATCHERS: Array<[LinkProvider, RegExp]> = [
  ["youtube", /(^|\.)youtube\.com$|(^|\.)youtu\.be$/],
  ["instagram", /(^|\.)instagram\.com$/],
  ["tiktok", /(^|\.)tiktok\.com$/],
  ["facebook", /(^|\.)facebook\.com$|(^|\.)fb\.watch$/],
  ["x", /(^|\.)x\.com$|(^|\.)twitter\.com$/],
  ["spotify", /(^|\.)spotify\.com$/],
];

/**
 * Trims and, when the pasted text has no scheme (e.g. "youtube.com/watch?…"),
 * prefixes "https://" so `new URL()` and the unfurl API both accept it.
 * Returns "" unchanged for an empty slot.
 */
export function normalizeLinkUrl(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

/**
 * Best-effort provider detection from a URL's PARSED HOSTNAME (anchored —
 * never a substring match on the full URL, or "example.com/?ref=youtube.com"
 * would classify as YouTube and bake a wrong video treatment into the
 * persisted snapshot). Never throws.
 */
export function detectLinkProvider(url: string): LinkProvider {
  let host: string;
  try {
    host = new URL(normalizeLinkUrl(url)).hostname.toLowerCase();
  } catch {
    return "generic";
  }
  for (const [provider, matcher] of PROVIDER_HOST_MATCHERS) {
    if (matcher.test(host)) return provider;
  }
  return "generic";
}

/** True for providers whose links are primarily video (drives the email
 *  play-strip affordance and the editor's play badge). */
export function isVideoProvider(provider: LinkProvider | undefined): boolean {
  return provider === "youtube" || provider === "tiktok";
}

/**
 * URL-pattern video detection for providers that mix photo and video content
 * under one host (so `isVideoProvider` alone can't classify them). Matches
 * the PARSED hostname + pathname — never a substring on the full URL — so
 * "example.com/reel/1" stays non-video. Covers the case where the page's
 * `og:video` tag is unavailable (e.g. Instagram serving a login wall to
 * datacenter IPs). Never throws.
 */
export function looksLikeVideoUrl(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(normalizeLinkUrl(url));
  } catch {
    return false;
  }
  const host = parsed.hostname.toLowerCase();
  const path = parsed.pathname;
  if (/(^|\.)instagram\.com$/.test(host)) {
    return /^\/(reels?|tv)\//.test(path);
  }
  if (/(^|\.)facebook\.com$/.test(host)) {
    return /^\/(reels?|watch)(\/|$)/.test(path) || /\/videos\//.test(path);
  }
  if (/(^|\.)fb\.watch$/.test(host)) return true;
  return false;
}

/** Domain for display, e.g. "youtube.com" (strips a leading "www.").
 *  Falls back to the raw input when it isn't parseable as a URL. */
export function linkDomain(url: string): string {
  try {
    return new URL(normalizeLinkUrl(url)).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

/** Titles a login wall or bot block serves instead of real page content —
 *  worthless as a snapshot title regardless of which URL produced them. */
const LOW_SIGNAL_LINK_TITLES = new Set([
  "instagram",
  "facebook",
  "tiktok",
  "youtube",
  "x",
  "twitter",
  "spotify",
]);

/**
 * True when a `<title>`-derived string carries no real content signal for
 * this URL — it merely names the platform (login walls serve
 * "<title>Instagram</title>"), repeats the domain, or is a login prompt.
 * Used by the unfurl fetcher to refuse baking such a title into the doc
 * snapshot as `status: "ok"`, and by `toLinkRenderItem` to un-poison
 * snapshots persisted before this guard existed. `undefined`/empty titles
 * return false — absence is handled by `unfurlStatus` itself.
 */
export function isLowSignalLinkTitle(
  title: string | undefined,
  url: string
): boolean {
  const normalized = (title ?? "").trim().toLowerCase();
  if (!normalized) return false;
  const domain = linkDomain(url).toLowerCase();
  if (normalized === domain || normalized === domain.split(".")[0]) {
    return true;
  }
  if (LOW_SIGNAL_LINK_TITLES.has(normalized)) return true;
  // "Log in", "Login • Instagram", "Log in or sign up to view", …
  if (/^log ?in\b/.test(normalized)) return true;
  return false;
}

/**
 * Providers whose OG image URLs are signed and expiring (Meta's scontent
 * CDN). A raw URL from them baked into a doc 403s in delivered email days
 * later, so the unfurl route drops the image instead when Cloudflare
 * re-hosting fails.
 */
export function hasExpiringImageUrls(
  provider: LinkProvider | undefined
): boolean {
  return provider === "instagram" || provider === "facebook";
}

/**
 * Syntactic screen for URLs this platform will fetch server-side on a user's
 * behalf: http(s) only, no embedded credentials, no non-standard ports, no
 * localhost/.local/.internal, no raw IP literal in the hostname. This alone
 * is NOT sufficient SSRF protection (a public hostname can still resolve to
 * a private address) — server callers must pair it with a DNS-level check
 * (see `resolvesToPublicAddress` in og-unfurl.ts).
 */
export function isPublicHttpUrlSyntax(raw: string): boolean {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return false;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return false;
  if (url.username || url.password) return false;
  if (url.port && url.port !== "80" && url.port !== "443") return false;
  const host = url.hostname.toLowerCase();
  if (
    host === "localhost" ||
    host.endsWith(".local") ||
    host.endsWith(".internal")
  ) {
    return false;
  }
  if (host.startsWith("[") || /^\d{1,3}(\.\d{1,3}){3}$/.test(host)) {
    return false;
  }
  return true;
}

/** The named entities OG text actually carries; `amp` is deliberately absent — it decodes LAST. */
const NAMED_HTML_ENTITIES: Record<string, string> = {
  quot: '"',
  lt: "<",
  gt: ">",
  apos: "'",
  nbsp: " ",
  hellip: "…",
  mdash: "—",
  ndash: "–",
  lsquo: "‘",
  rsquo: "’",
  ldquo: "“",
  rdquo: "”",
};

function isRenderableCodePoint(cp: number): boolean {
  return cp > 0 && cp <= 0x10ffff && !(cp >= 0xd800 && cp <= 0xdfff);
}

/**
 * Decodes the entity subset OpenGraph/Twitter/<title> text carries — numeric
 * (`&#39;`, `&#x27;`, `&#X27;`) and the named set above. Unknown named
 * entities and unrenderable code points pass through untouched; `&amp;` is
 * decoded last so `&amp;#x27;` yields the literal `&#x27;`, never `'`.
 */
export function decodeHtmlEntities(s: string): string {
  return s
    .replace(/&#(?:x([0-9a-f]+)|(\d+));/gi, (entity, hex, dec) => {
      const cp = hex ? parseInt(hex, 16) : parseInt(dec, 10);
      return isRenderableCodePoint(cp) ? String.fromCodePoint(cp) : entity;
    })
    .replace(
      /&([a-z]+);/gi,
      (entity, name) => NAMED_HTML_ENTITIES[name] ?? entity
    )
    .replace(/&amp;/g, "&")
    .trim();
}
